import { useState, useMemo, FormEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { v4 as uuidv4 } from 'uuid'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxToolkit'
import { addConversation, addMessage } from '../../reduxToolkit/conversationsSlice'
import {
  selectAliceConversationsWithMessages,
  ALICE_TOPIC_TYPE,
} from '../../reduxToolkit/conversationsSelectors'
import { formatDateInTimeZone, parseDateInTimeZone } from '../../../lib/formatDateInTimeZone'

import './index.scss'

export function AliceIndex() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { membershipId, userId } = useAppSelector((s) => s.membership)

  const allConversations = useAppSelector(selectAliceConversationsWithMessages)

  const [showClosed, setShowClosed] = useState(false)
  const [newMessage, setNewMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const conversations = useMemo(
    () => allConversations.filter((c) => (showClosed ? c.status === 'CLOSED' : c.status !== 'CLOSED')),
    [allConversations, showClosed]
  )
  const closedCount = useMemo(
    () => allConversations.filter((c) => c.status === 'CLOSED').length,
    [allConversations]
  )

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (!newMessage.trim() || isSubmitting) return

    setIsSubmitting(true)

    const now = new Date().toISOString()
    const conversationId = uuidv4()
    const messageId = uuidv4()

    // Create a new conversation
    dispatch(
      addConversation({
        id: conversationId,
        membership_id: membershipId,
        provider_id: null,
        topic_type: ALICE_TOPIC_TYPE,
        topic_ref_id: conversationId, // Self-referential for Alice conversations
        status: 'OPEN',
        created_at: now,
        updated_at: now,
        deleted_at: null,
        last_message_at: now,
        last_message_direction: 'INBOUND',
      })
    )

    // Create the first message
    dispatch(
      addMessage({
        id: messageId,
        conversation_id: conversationId,
        membership_id: membershipId,
        direction: 'INBOUND',
        channel: 'in-app',
        body_text: newMessage.trim(),
        body_html: null,
        subject: null,
        from_address: null,
        to_address: null,
        status: 'DELIVERED',
        sent_at: now,
        read_at: null,
        scheduled_at: null,
        attachments: null,
        created_at: now,
        updated_at: now,
        deleted_at: null,
        error: null,
        external_message_id: null,
        headers: null,
      })
    )

    setNewMessage('')

    // Navigate to the conversation — no need to reset isSubmitting
    // since the component unmounts on navigation
    navigate(`/alice/${conversationId}`)
  }

  return (
    <div className="alice-index">
      <div className="alice-index__header">
        <h1>
          <i className="material-icons-outlined">support_agent</i>
          Alice
        </h1>
        <p className="alice-index__subtitle">
          Your AI assistant for managing healthcare reimbursements
        </p>
      </div>

      <form className="alice-index__new-conversation" onSubmit={handleSubmit}>
        <div className="alice-index__input-wrapper">
          <textarea
            className="form-control"
            placeholder="Ask Alice anything about your reimbursements..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            rows={3}
            disabled={isSubmitting}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!newMessage.trim() || isSubmitting}
          >
            <i className="material-icons">send</i>
            <span>Start Conversation</span>
          </button>
        </div>
      </form>

      <div className="alice-index__conversations">
        <div className="alice-index__conversations-header">
          <h2>{showClosed ? 'Closed Conversations' : 'Conversations'}</h2>
          {closedCount > 0 && (
            <button
              type="button"
              className="btn btn-link btn-sm alice-index__toggle-closed"
              onClick={() => setShowClosed(!showClosed)}
            >
              {showClosed ? 'Show open' : `Show closed (${closedCount})`}
            </button>
          )}
        </div>
        {conversations.length === 0 ? (
          <div className="alice-index__empty">
            <p>{showClosed ? 'No closed conversations.' : 'No conversations yet. Start one above!'}</p>
          </div>
        ) : (
          <div className="alice-index__list">
            {conversations.map((conversation) => {
              const firstMessage = conversation.messages[0]
              const lastMessage = conversation.messages[conversation.messages.length - 1]
              const preview = firstMessage?.body_text?.slice(0, 100) || 'No messages'

              return (
                <Link
                  key={conversation.id}
                  to={`/alice/${conversation.id}`}
                  className={`alice-index__conversation-item ${conversation.status === 'CLOSED' ? 'alice-index__conversation-item--closed' : ''}`}
                >
                  <div className="alice-index__conversation-preview">
                    {preview}
                    {(firstMessage?.body_text?.length ?? 0) > 100 && '...'}
                  </div>
                  <div className="alice-index__conversation-meta">
                    <span className="alice-index__message-count">
                      {conversation.messages.length} message{conversation.messages.length !== 1 && 's'}
                    </span>
                    <span className="alice-index__conversation-date">
                      {formatDateInTimeZone(
                        parseDateInTimeZone(lastMessage?.created_at || conversation.created_at),
                        { format: 'MMM d, yyyy h:mm a' }
                      )}
                    </span>
                  </div>
                </Link>
              )
            })}
          </div>
        )}
      </div>

      <div className="alice-index__disclaimer">
        <p>
          Alice can make mistakes.  By interacting with Alice, you agree to the{' '}
          <a href="https://www.healthsharetech.com" target="_blank" rel="noopener noreferrer">AI Agents Addendum</a>{' '}
          of our Terms of Service.
          You can opt out of having your conversation data used for training at any time from the{' '}
          <Link to="/settings">Settings</Link> page.
        </p>
      </div>
    </div>
  )
}
